import { supabase } from './supabase';

/**
 * Client for the site-factory edge function: provisions a business website
 * (GitHub repo in the platform org + Vercel project) from the starter
 * template and reports provisioning progress.
 */

export type SiteProjectStatus = 'provisioning' | 'deploying' | 'live' | 'failed';

export interface SiteProject {
  id: string;
  business_id: string;
  github_repo: string | null;
  vercel_project_id: string | null;
  preview_url: string | null;
  status: SiteProjectStatus;
  error: string | null;
  created_at: string;
  updated_at: string;
}

/** Thrown when the business has no active hosting subscription yet. */
export class HostingRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HostingRequiredError';
  }
}

function invoke(body: Record<string, unknown>) {
  if (!supabase) throw new Error('Supabase client not initialized');
  return supabase.functions.invoke('site-factory', { body });
}

async function readError(error: unknown, data: any): Promise<{ message: string; code?: string }> {
  if (data?.error && typeof data.error === 'string') return { message: data.error, code: data.code };
  const ctx = (error as any)?.context;
  if (ctx?.text) {
    try {
      const parsed = JSON.parse(await ctx.text());
      if (parsed?.error) return { message: parsed.error, code: parsed.code };
    } catch { /* not JSON */ }
  }
  return { message: (error as any)?.message || 'Request failed' };
}

/** Start provisioning (idempotent — safe to call again after a failure). */
export async function createSite(businessId: string): Promise<SiteProject> {
  const { data, error } = await invoke({ businessId, action: 'create' });
  if (error || !data?.success) {
    const err = await readError(error, data);
    if (err.code === 'hosting_required') throw new HostingRequiredError(err.message);
    throw new Error(err.message);
  }
  return data.site as SiteProject;
}

export interface RepoConnectionStatus {
  state: 'working' | 'broken' | 'none';
  repo: string | null;
  branch: string | null;
  message: string;
}

export interface ConnectedSite extends SiteProject {
  botId?: string | null;
  warning?: string | null;
  connection?: RepoConnectionStatus | null;
}

/**
 * Bind an existing repository to a business and provision its web manager.
 * A Vercel project is optional. Super admin only; this never creates a repository.
 */
export async function connectWebsite(
  businessId: string,
  githubRepo: string,
  vercelProjectId?: string,
): Promise<ConnectedSite> {
  const { data, error } = await invoke({ businessId, action: 'connect', githubRepo, vercelProjectId });
  if (error || !data?.success) throw new Error((await readError(error, data)).message);
  return {
    ...(data.site as SiteProject),
    botId: data.botId as string | null,
    warning: (data.warning as string | null) ?? null,
    connection: (data.connection as RepoConnectionStatus | null) ?? null,
  };
}

let repoListPromise: Promise<string[]> | null = null;

/** Repositories the platform GitHub token can connect. Super admin only. Cached for the session. */
export function listGithubRepos(): Promise<string[]> {
  if (repoListPromise) return repoListPromise;
  const pending = (async () => {
    const { data, error } = await invoke({ action: 'repos' });
    if (error || !data?.success) throw new Error((await readError(error, data)).message);
    const repos = Array.isArray(data.repos) ? data.repos : [];
    return repos.filter((repo: unknown): repo is string => typeof repo === 'string' && repo.trim().length > 0);
  })();
  repoListPromise = pending;
  pending.catch(() => {
    if (repoListPromise === pending) repoListPromise = null;
  });
  return pending;
}

/** Live check of the repository already saved for this business. Super admin only. */
export async function getRepoConnection(businessId: string): Promise<RepoConnectionStatus> {
  const { data, error } = await invoke({ businessId, action: 'connection' });
  if (error || !data?.success) throw new Error((await readError(error, data)).message);
  return data.connection as RepoConnectionStatus;
}

/** Unbind the repository and retire the web-developer bot. Super admin only. */
export async function disconnectWebsite(businessId: string): Promise<void> {
  const { data, error } = await invoke({ businessId, action: 'disconnect' });
  if (error || !data?.success) throw new Error((await readError(error, data)).message);
}

/** Current provisioning state; null when no site has been started. */
export async function getSiteStatus(businessId: string): Promise<SiteProject | null> {
  const { data, error } = await invoke({ businessId, action: 'status' });
  if (error || !data?.success) throw new Error((await readError(error, data)).message);
  return (data.site as SiteProject | null) ?? null;
}
